import { createContext, memo, useContext, useMemo, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { TablesDataType } from '../../dental-insurance-eligibility-report/dental-insurance-eligibility-report/types/types';

type AccordionId = keyof TablesDataType;

type InsuranceReportAccordionContextState = {
  isPrintReport: boolean;
  activeAccordionIds: Set<AccordionId>;
  accordionIds: AccordionId[];
  toggleAccordion: (accordionId: AccordionId) => void;
};

export const InsuranceReportAccordionContext = createContext<InsuranceReportAccordionContextState>({
  isPrintReport: false,
  activeAccordionIds: new Set<AccordionId>(),
  toggleAccordion: () => {},
  accordionIds: [],
});

type InsuranceReportAccordionProviderProps = {
  children: React.ReactNode;
  accordionIds: AccordionId[];
  isPrintReport?: boolean;
  activeAccordionId?: string;
};
export const InsuranceReportAccordionProvider = memo(
  ({
    children,
    isPrintReport = false,
    accordionIds,
    activeAccordionId = '',
  }: InsuranceReportAccordionProviderProps) => {
    const [activeAccordionIds, setActiveAccordionIds] = useState<Set<AccordionId>>(
      () => new Set([...accordionIds, activeAccordionId] as AccordionId[])
    );

    useEffect(() => {
      if (accordionIds.length > 0) {
        setActiveAccordionIds(new Set([...accordionIds, activeAccordionId] as AccordionId[]));
      }
    }, [accordionIds, activeAccordionId]);

    const toggleAccordion = useMemo(
      () => (accordionId: AccordionId) => {
        setActiveAccordionIds((prevIds) => {
          const newIds = new Set(prevIds);
          if (newIds.has(accordionId)) {
            newIds.delete(accordionId);
          } else {
            newIds.add(accordionId);
          }
          return newIds;
        });
      },
      []
    );

    const providerValue = useMemo(() => {
      return {
        isPrintReport,
        activeAccordionIds,
        accordionIds,
        toggleAccordion,
      };
    }, [activeAccordionIds, isPrintReport, toggleAccordion]);

    return (
      <InsuranceReportAccordionContext.Provider value={providerValue}>
        <section css={accordionSectionStyles}>{children}</section>
      </InsuranceReportAccordionContext.Provider>
    );
  }
);

const accordionSectionStyles = css({ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) });

export const useInsuranceReportAccordionContext = () => {
  const context = useContext(InsuranceReportAccordionContext);
  if (!context) {
    throw new Error('useInsuranceReportAccordionContext must be used within an InsuranceReportAccordionProvider');
  }
  return context;
};

InsuranceReportAccordionProvider.displayName = 'InsuranceReportAccordionProvider';
